import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { fetchWithAuth } from '@/utils/api';
import { formatEIN } from '@/utils/formatters';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/PageHeader';
import Spinner from '@/components/Spinner';
import {
  FileText, Shield, HeartPulse, Landmark, Building2, Users,
  ClipboardList, Mail, BookOpen, FilePen, Home, Car,
  Printer, DollarSign, Network
} from 'lucide-react';

// ==================== SECTION DATA ====================

const SECTION_TABS = [
  { id: 'trust-documents', number: 1, title: 'Trust Documents', icon: FileText,
    description: 'Declaration of Trust, amendments, restatements, and organizational meeting minutes.' },
  { id: 'powers-of-attorney', number: 2, title: 'Powers of Attorney', icon: Shield,
    description: 'Financial Power of Attorney, Healthcare Power of Attorney, and HIPAA authorizations.' },
  { id: 'healthcare-directives', number: 3, title: 'Healthcare Directives', icon: HeartPulse,
    description: 'Living will, advance directive, POLST/DNR forms, and medical authorizations.' },
  { id: 'financial-accounts', number: 4, title: 'Financial Accounts', icon: Landmark,
    description: 'Bank accounts, investment accounts, retirement accounts, and debt records.' },
  { id: 'property-assets', number: 5, title: 'Property & Assets', icon: Building2,
    description: 'Real estate deeds, vehicle titles, business interests, and personal property records.' },
  { id: 'key-contacts', number: 6, title: 'Key Contacts', icon: Users,
    description: 'Attorney, CPA, financial advisor, insurance agent, executor, and trustee contacts.' },
  { id: 'resolutions-minutes', number: 7, title: 'Resolutions & Minutes', icon: ClipboardList,
    description: 'Trustee meeting minutes, formal resolutions, and decision records.' },
  { id: 'correspondence', number: 8, title: 'Correspondence', icon: Mail,
    description: 'Letters to and from beneficiaries, professionals, institutions, and government agencies.' },
  { id: 'money', number: 9, title: 'Financial Records', icon: DollarSign,
    description: 'Transaction ledger summary, distribution history, compensation summary, and investment portfolio.' },
  { id: 'structure', number: 10, title: 'Entity Structure', icon: Network,
    description: 'Entity list, beneficiary roster, Schedule A assets, and communications log summary.' },
];

const QUICK_REFERENCE = {
  duties: [
    { title: 'Duty of Loyalty', text: 'Act solely in the best interest of the beneficiaries. Never use trust assets for personal benefit.' },
    { title: 'Duty of Care', text: 'Manage trust assets with the same care a prudent person would use for their own property.' },
    { title: 'Duty of Impartiality', text: 'Treat all beneficiaries fairly. Do not favor one beneficiary over another unless the trust document directs it.' },
    { title: 'Duty to Inform', text: 'Keep beneficiaries reasonably informed about the trust\'s status, assets, and administration.' },
  ],
  decisionFilter: [
    'Is this action in the best interest of ALL beneficiaries?',
    'Does the trust document authorize this action?',
    'Is this a prudent use of trust assets?',
    'Have I documented my reasoning?',
    'Should I consult the trust attorney first?',
  ],
  signingRule: 'Always sign as "Trustee of the [Trust Name]" — never individually. Include the date and trust name on every document.',
  emergency: ['A lawsuit is filed against the trust or trustee', 'A beneficiary disputes a trust decision', 'A significant asset change occurs (sale, purchase, damage)', 'You are unsure whether an action is authorized'],
};

// ==================== PRINT STYLES ====================

const PRINT_STYLES = `
@media print {
  /* Hide everything except the printable area */
  body * { visibility: hidden; }
  .printable-area, .printable-area * { visibility: visible; }
  .printable-area {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    padding: 0.75in 0.75in 0.75in 1.5in;
  }
  .no-print { display: none !important; }

  /* Serif headings — legal-instrument typography */
  .printable-area h1, .printable-area h2, .printable-area h3 {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-weight: 700;
    letter-spacing: 0.01em;
  }

  /* Suppress browser default headers/footers (URL, date, page numbers) */
  @page {
    size: letter;
    margin: 0;
  }

  /* Section divider specific */
  .section-divider-print {
    page-break-after: always;
    display: flex !important;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 85vh;
    text-align: center;
  }

  .section-divider-print h1 {
    font-size: 28pt !important;
    margin-bottom: 8pt !important;
  }

  .section-divider-print .section-number {
    font-size: 72pt !important;
    font-weight: 700 !important;
    opacity: 0.15 !important;
    margin-bottom: 12pt !important;
  }

  .section-divider-print .section-desc {
    font-size: 12pt !important;
    max-width: 400pt !important;
    line-height: 1.5 !important;
  }

  /* Quick reference specific */
  .quick-ref-print {
    page-break-after: always;
    font-size: 10pt !important;
  }

  .quick-ref-print h2 {
    font-size: 16pt !important;
  }

  .quick-ref-print h3 {
    font-size: 12pt !important;
  }
}
`;

// ==================== COMPONENT ====================

const PrintableBinderPage = () => {
  const { user, selectedTrust } = useAuth();
  const [coverData, setCoverData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePrint, setActivePrint] = useState(null);
  const [moneyData, setMoneyData] = useState(null);
  const [structureData, setStructureData] = useState(null);
  const [sectionDataLoading, setSectionDataLoading] = useState(false);

  useEffect(() => {
    const fetchCoverData = async () => {
      if (!selectedTrust) { setLoading(false); return; }
      try {
        const response = await fetchWithAuth('/binder/cover-sheet-data');
        if (response.ok) {
          const data = await response.json();
          setCoverData(data);
        }
      } catch (e) {
        console.error('Failed to load binder data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchCoverData();
  }, [selectedTrust]);

  // Fetch Money and Structure section data on demand
  const fetchSectionData = async (sectionId) => {
    if (!selectedTrust) return;
    setSectionDataLoading(true);
    try {
      if (sectionId === 'money' && !moneyData) {
        const params = `?trust_id=${encodeURIComponent(selectedTrust.trust_id)}`;
        const [txnsRes, distsRes, compRes, invRes] = await Promise.all([
          fetchWithAuth(`/transactions${params}`),
          fetchWithAuth(`/distributions${params}`),
          fetchWithAuth(`/compensation-payments${params}`),
          fetchWithAuth(`/trusts/${encodeURIComponent(selectedTrust.trust_id)}/investments`),
        ]);

        let transactions = [];
        if (txnsRes.ok) {
          const txnsJson = await txnsRes.json();
          transactions = Array.isArray(txnsJson) ? txnsJson : (txnsJson.items || []);
        }

        let distributions = [];
        if (distsRes.ok) {
          const distsJson = await distsRes.json();
          distributions = distsJson.items || [];
        }

        let compensation = [];
        if (compRes.ok) {
          const compJson = await compRes.json();
          compensation = compJson.items || [];
        }

        let investments = { investments: [], total_cost_basis: 0, total_current_value: 0, total_return: 0, total_return_pct: 0 };
        if (invRes.ok) {
          investments = await invRes.json();
        }

        // Compute transaction summary
        const totalInflows = transactions
          .filter(t => t.direction === 'inflow' || t.direction === 'in')
          .reduce((sum, t) => sum + (t.amount || 0), 0);
        const totalOutflows = transactions
          .filter(t => t.direction === 'outflow' || t.direction === 'out')
          .reduce((sum, t) => sum + (t.amount || 0), 0);

        // Compute compensation total
        const compTotal = compensation.reduce((sum, c) => sum + (c.amount || 0), 0);

        // Compute distribution total
        const distTotal = distributions.reduce((sum, d) => sum + (d.amount || 0), 0);

        setMoneyData({
          transactions,
          distributions,
          compensation,
          investments: investments.investments || [],
          investmentSummary: {
            total_cost_basis: investments.total_cost_basis || 0,
            total_current_value: investments.total_current_value || 0,
            total_return: investments.total_return || 0,
            total_return_pct: investments.total_return_pct || 0,
          },
          summary: { totalInflows, totalOutflows, netFlow: totalInflows - totalOutflows, compTotal, distTotal },
        });
      } else if (sectionId === 'structure' && !structureData) {
        const params = `?trust_id=${encodeURIComponent(selectedTrust.trust_id)}`;
        const [entitiesRes, beneRes, schedRes, commRes] = await Promise.all([
          fetchWithAuth(`/entities${params}`),
          fetchWithAuth(`/beneficiaries/dashboard${params}`),
          fetchWithAuth(`/schedule-a${params}`),
          fetchWithAuth(`/trusts/${encodeURIComponent(selectedTrust.trust_id)}/communications?limit=200`),
        ]);

        let entities = [];
        if (entitiesRes.ok) {
          const entitiesJson = await entitiesRes.json();
          entities = entitiesJson.items || [];
        }

        let beneficiaries = [];
        let classBeneficiaries = [];
        if (beneRes.ok) {
          const beneJson = await beneRes.json();
          beneficiaries = beneJson.beneficiaries || [];
          classBeneficiaries = beneJson.class_beneficiaries || [];
        }

        let scheduleA = [];
        if (schedRes.ok) {
          const schedJson = await schedRes.json();
          scheduleA = schedJson.items || [];
        }

        let communications = [];
        if (commRes.ok) {
          const commJson = await commRes.json();
          communications = commJson.items || [];
        }

        setStructureData({ entities, beneficiaries, classBeneficiaries, scheduleA, communications });
      }
    } catch (e) {
      console.error('Failed to load section data:', e);
    } finally {
      setSectionDataLoading(false);
    }
  };

  const handlePrint = (sectionId) => {
    // Pre-fetch data for money/structure sections
    if (sectionId === 'money' || sectionId === 'structure') {
      fetchSectionData(sectionId);
    }
    setActivePrint(sectionId);
    setTimeout(() => {
      window.print();
      setActivePrint(null);
    }, 100);
  };

  if (!selectedTrust) {
    return (
      <div className="card-trust corner-mark p-12 flex flex-col items-center justify-center">
        <FileText className="w-12 h-12 text-navy/30 mb-3"/>
        <h2 className="text-xl font-semibold text-navy mb-1">Select a trust</h2>
        <p className="text-sm text-muted-foreground">Choose a trust to view the Record Book.</p>
      </div>
    );
  }

  // ==================== RENDER ====================

  return (
    <>
      <style>{PRINT_STYLES}</style>
      <div className="no-print">
        <PageHeader
          title="Printable Record Book"
          subtitle="Organize your trust documents with printable inserts — print on standard letter-size paper, use a 3-ring binder with tab dividers for best results"
          help={{
            items: [
              { text: 'Print cover sheets, tab dividers, and reference cards for your physical trust binder' },
              { text: 'Use a 3-ring binder with tab dividers for best results' },
              { text: 'Print on standard letter-size paper' },
            ],
            taPrompt: 'How do I set up a physical trust compliance binder?',
          }}
        />

          {/* COVER SHEET SECTION */}
          <div className="mb-10">
            <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-gold" aria-hidden="true" />
              Binder Cover Sheet
            </h2>
            <div className="card-trust corner-mark p-6">
              {loading ? (
                <Spinner size={32} label="Loading binder cover data" className="mx-auto my-4" />
              ) : (
                <>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-bold text-navy">
                        {coverData?.trust_name || 'Your Trust Name'}
                      </h3>
                      <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
                        {coverData?.trust_type && <div className="font-mono">{coverData.trust_type}</div>}
                        {coverData?.formation_date && <div className="font-mono">Formed: {coverData.formation_date}</div>}
                        {coverData?.ein && <div className="font-mono">EIN: {formatEIN(coverData.ein)}</div>}
                        {coverData?.jurisdiction && <div>Jurisdiction: {coverData.jurisdiction}</div>}
                        {coverData?.trustees?.length > 0 && (
                          <div>Trustee{coverData.trustees.length > 1 ? 's' : ''}: {coverData.trustees.join(', ')}</div>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => handlePrint('cover')}
                      className="btn-primary"
                      >
                      <Printer className="w-4 h-4" /> Print Cover Sheet
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* SECTION DIVIDERS */}
          <div className="mb-10">
            <h2 className="text-lg font-semibold text-navy mb-4">
              Section Tab Dividers
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Print each divider on cardstock or heavy paper and place behind a tab divider in your binder.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SECTION_TABS.map((section) => {
                const Icon = section.icon;
                return (
                  <div
                    key={section.id}
                    className="flex items-start gap-3 card-trust corner-mark p-4"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-gold/10 dark:bg-gold/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gold" aria-hidden="true" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono uppercase tracking-wider text-gold">Section {section.number}</span>
                        <h3 className="text-sm font-semibold text-navy">{section.title}</h3>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{section.description}</p>
                      <Button
                        onClick={() => handlePrint(`tab-${section.id}`)}
                        className="btn-secondary mt-2"
                      >
                        <Printer className="w-3 h-3" /> Print Divider
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* REFERENCE CARD */}
          <div className="mb-10">
            <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-gold" aria-hidden="true" />
              Trustee Quick Reference Card
            </h2>
            <div className="card-trust corner-mark p-6">
              <p className="text-sm text-muted-foreground mb-3">
                A single-page cheat sheet covering your fiduciary duties, decision filter, signing rules, and emergency protocols.
                Print this and keep it at the front of your binder.
              </p>
              <Button
                onClick={() => handlePrint('quick-ref')}
                      className="btn-primary"
                      >
                <Printer className="w-4 h-4" /> Print Reference Card
              </Button>
            </div>
          </div>

          {/* RESOLUTION TEMPLATE */}
          <div className="mb-10">
            <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
              <FilePen className="w-5 h-5 text-gold" aria-hidden="true" />
              Resolution Template
            </h2>
            <div className="card-trust corner-mark p-6">
              <p className="text-sm text-muted-foreground mb-3">
                A blank WHEREAS/BE IT RESOLVED template with signature, witness, and notary blocks.
                Print a fresh copy each time you need to record a formal trust resolution.
              </p>
              <Button
                onClick={() => handlePrint('resolution')}
                      className="btn-primary"
                      >
                <Printer className="w-4 h-4" /> Print Resolution Template
              </Button>
            </div>
          </div>

          {/* TRANSFER CHECKLISTS */}
          <div className="mb-10">
            <h2 className="text-lg font-semibold text-navy mb-4">
              Asset Transfer Checklists
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Step-by-step checklists for transferring specific asset types into or out of your trust. Print when you're ready to start a transfer.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-start gap-3 card-trust corner-mark p-4">
                <div className="flex-shrink-0 w-10 h-10 bg-gold/10 dark:bg-gold/20 flex items-center justify-center">
                  <Home className="w-5 h-5 text-gold" aria-hidden="true" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-navy">Real Estate Transfer</h3>
                  <p className="text-xs text-muted-foreground mt-1">Deed recording, insurance, mortgage, and tax steps.</p>
                  <Button
                    onClick={() => handlePrint('checklist-real-estate')}
                    className="btn-secondary mt-2"
                  >
                    <Printer className="w-3 h-3" /> Print Checklist
                  </Button>
                </div>
              </div>
              <div className="flex items-start gap-3 card-trust corner-mark p-4">
                <div className="flex-shrink-0 w-10 h-10 bg-gold/10 dark:bg-gold/20 flex items-center justify-center">
                  <Car className="w-5 h-5 text-gold" aria-hidden="true" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-navy">Vehicle Transfer</h3>
                  <p className="text-xs text-muted-foreground mt-1">Title transfer, DMV, insurance, and registration steps.</p>
                  <Button
                    onClick={() => handlePrint('checklist-vehicle')}
                    className="btn-secondary mt-2"
                  >
                    <Printer className="w-3 h-3" /> Print Checklist
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* FINANCIAL RECORDS (MONEY) SECTION */}
          <div className="mb-10">
            <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-gold" aria-hidden="true" />
              Financial Records
            </h2>
            <div className="card-trust corner-mark p-6">
              <p className="text-sm text-muted-foreground mb-3">
                Transaction ledger summary, distribution history, compensation summary, and investment portfolio.
                Print this section for a comprehensive financial snapshot of your trust.
              </p>
              <Button
                onClick={() => handlePrint('money')}
                      className="btn-primary"
                      >
                <Printer className="w-4 h-4" /> Print Financial Records
              </Button>
            </div>
          </div>

          {/* ENTITY STRUCTURE SECTION */}
          <div className="mb-10">
            <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
              <Network className="w-5 h-5 text-gold" aria-hidden="true" />
              Entity Structure
            </h2>
            <div className="card-trust corner-mark p-6">
              <p className="text-sm text-muted-foreground mb-3">
                Entity list, beneficiary roster, Schedule A assets, and communications log summary.
                Print this section for a structural overview of your trust.
              </p>
              <Button
                onClick={() => handlePrint('structure')}
                      className="btn-primary"
                      >
                <Printer className="w-4 h-4" /> Print Entity Structure
              </Button>
            </div>
          </div>
      </div>

      {/* ==================== PRINTABLE AREAS ==================== */}
      <div className="printable-area" style={{ display: activePrint ? 'block' : 'none' }}>

        {/* COVER SHEET */}
        {activePrint === 'cover' && (
          <div className="p-8" style={{ paddingLeft: '1.5in', paddingTop: '1in' }}>
            <div className="text-center border-2 border-navy/30 p-12" style={{ minHeight: '8in' }}>
              <div className="mb-8 pt-4">
                <div className="text-xs tracking-widest text-navy/40 uppercase mb-2">Trust Governance Workspace</div>
                <div className="w-16 h-0.5 bg-gold mx-auto mb-6"></div>
              </div>
              <h1 className="text-3xl font-bold text-navy mb-2" style={{ fontSize: '28pt' }}>
                TRUST COMPLIANCE BINDER
              </h1>
              <div className="w-24 h-0.5 bg-gold mx-auto my-6"></div>
              <div className="mt-8 space-y-3" style={{ fontSize: '14pt' }}>
                <div className="text-xl font-semibold font-mono text-navy">{coverData?.trust_name || '_________________'}</div>
                {coverData?.trust_type && <div className="font-mono text-navy/60">{coverData.trust_type}</div>}
                {coverData?.formation_date && <div className="font-mono text-navy/60">Formed: {coverData.formation_date}</div>}
                {coverData?.ein && <div className="font-mono text-navy/60">EIN: {formatEIN(coverData.ein)}</div>}
                {coverData?.jurisdiction && <div className="font-mono text-navy/60">Jurisdiction: {coverData.jurisdiction}</div>}
                {coverData?.trustees?.length > 0 && (
                  <div className="font-mono text-navy/60">Trustee{coverData.trustees.length > 1 ? 's' : ''}: {coverData.trustees.join(', ')}</div>
                )}
              </div>
              <div className="mt-16 pt-8 border-t border-navy/20">
                <div className="text-xs text-navy/40">
                  Maintained with TrustOffice — trustoffice.app
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SECTION DIVIDERS */}
        {SECTION_TABS.map((section) => (
          activePrint === `tab-${section.id}` && (
            <div key={section.id} className="section-divider-print p-8" style={{ paddingLeft: '1.5in', paddingTop: '0.75in' }}>
              <div className="flex flex-col items-center justify-center" style={{ minHeight: '700px' }}>
                <div className="section-number font-mono" style={{ fontSize: '72pt', fontWeight: 700, color: 'rgba(213, 173, 54, 0.15)' }}>
                  {section.number}
                </div>
                <h1 className="text-4xl font-bold text-navy mb-4" style={{ fontSize: '28pt' }}>
                  {section.title}
                </h1>
                <div className="w-32 h-0.5 bg-gold mb-6"></div>
                <p className="text-muted-foreground text-center max-w-md" style={{ fontSize: '12pt', lineHeight: 1.6 }}>
                  {section.description}
                </p>
                <div className="mt-8 w-full max-w-md pt-6 border-t border-navy/20">
                  <p className="text-xs text-navy/40 text-center">Place behind tab divider {section.number} in your trust compliance binder.</p>
                </div>
              </div>
              <div className="text-center mt-8 text-xs text-navy/40">
                TrustOffice — trustoffice.app
              </div>
            </div>
          )
        ))}

        {/* TRUSTEE QUICK REFERENCE CARD */}
        {activePrint === 'quick-ref' && (
          <div className="quick-ref-print p-8" style={{ paddingLeft: '1.5in', paddingTop: '0.75in' }}>
            <div className="max-w-lg mx-auto">
              <h1 className="text-2xl font-bold text-navy mb-1" style={{ fontSize: '18pt' }}>
                Trustee Quick Reference
              </h1>
              <div className="w-16 h-0.5 bg-gold mb-6 mt-2"></div>

              <h2 className="text-lg font-semibold text-navy mb-3" style={{ fontSize: '13pt' }}>Fiduciary Duties</h2>
              {QUICK_REFERENCE.duties.map((d, i) => (
                <div key={i} className="mb-3 pl-3 border-l-2 border-warning">
                  <div className="font-semibold text-navy" style={{ fontSize: '11pt' }}>{d.title}</div>
                  <div className="text-navy/60" style={{ fontSize: '10pt' }}>{d.text}</div>
                </div>
              ))}

              <h2 className="text-lg font-semibold text-navy mb-3 mt-6" style={{ fontSize: '13pt' }}>Decision Filter</h2>
              <p className="text-navy/60 mb-2" style={{ fontSize: '10pt' }}>
                Before taking any action as trustee, ask yourself:
              </p>
              {QUICK_REFERENCE.decisionFilter.map((q, i) => (
                <div key={i} className="flex items-start gap-2 mb-1">
                  <span className="text-gold font-bold" style={{ fontSize: '10pt' }}>✓</span>
                  <span className="text-navy" style={{ fontSize: '10pt' }}>{q}</span>
                </div>
              ))}
              <p className="mt-2 text-muted-foreground italic" style={{ fontSize: '9pt' }}>
                If you cannot answer "yes" confidently, seek professional guidance before proceeding.
              </p>

              <h2 className="text-lg font-semibold text-navy mb-2 mt-6" style={{ fontSize: '13pt' }}>Signing Rule</h2>
              <p className="text-navy bg-subtle-bg p-3" style={{ fontSize: '10pt' }}>
                {QUICK_REFERENCE.signingRule}
              </p>

              <h2 className="text-lg font-semibold text-navy mb-2 mt-6" style={{ fontSize: '13pt' }}>Contact Your Attorney Immediately If:</h2>
              {QUICK_REFERENCE.emergency.map((e, i) => (
                <div key={i} className="flex items-start gap-2 mb-1">
                  <span className="text-warning font-bold" style={{ fontSize: '10pt' }}>⚠</span>
                  <span className="text-navy" style={{ fontSize: '10pt' }}>{e}</span>
                </div>
              ))}

              <div className="mt-8 pt-4 border-t border-navy/20 text-center">
                <p className="text-xs text-navy/40">TrustOffice — trustoffice.app</p>
              </div>
            </div>
          </div>
        )}

        {/* RESOLUTION TEMPLATE */}
        {activePrint === 'resolution' && (
          <div className="p-8" style={{ paddingLeft: '1.5in', paddingTop: '0.75in' }}>
            <div className="max-w-xl mx-auto">
              <h1 className="text-2xl font-bold text-center text-navy mb-2" style={{ fontSize: '16pt' }}>
                RESOLUTION
              </h1>
              <h2 className="text-center text-navy/60 mb-8" style={{ fontSize: '12pt' }}>
                OF {coverData?.trust_name ? <span className="font-semibold">{coverData.trust_name.toUpperCase()}</span> : <span className="border-b border-navy/60" style={{ minWidth: '200px', display: 'inline-block' }}>&nbsp;</span>}
              </h2>

              <div className="mb-6">
                <p className="text-navy/60 mb-3" style={{ fontSize: '10pt' }}>WHEREAS, the Trustee(s) of the above-named Trust deem it necessary and appropriate to take the following action;</p>
                <div className="mb-6" style={{ fontSize: '10pt' }}>
                  <div className="border-b border-navy/30 py-2 text-navy/40">Describe the reason or background for this resolution</div>
                  <div className="border-b border-navy/30 py-2 text-navy/40">Continue background as needed</div>
                  <div className="border-b border-navy/30 py-2 text-navy/40">&nbsp;</div>
                </div>
              </div>

              <div className="mb-8">
                <p className="text-navy/60 mb-3" style={{ fontSize: '10pt' }}>NOW, THEREFORE, BE IT RESOLVED that the Trustee(s) hereby authorize and direct the following action:</p>
                <div style={{ fontSize: '10pt' }}>
                  <div className="border-b border-navy/30 py-2 text-navy/40">Describe the specific action being authorized</div>
                  <div className="border-b border-navy/30 py-2 text-navy/40">Continue description as needed</div>
                  <div className="border-b border-navy/30 py-2 text-navy/40">&nbsp;</div>
                  <div className="border-b border-navy/30 py-2 text-navy/40">&nbsp;</div>
                </div>
              </div>

              <div className="mt-16 space-y-8" style={{ fontSize: '10pt' }}>
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <div className="border-b border-navy mb-1 h-8"></div>
                    <div className="text-navy/60">Trustee Signature</div>
                  </div>
                  <div>
                    <div className="border-b border-navy mb-1 h-8"></div>
                    <div className="text-navy/60">Date</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <div className="border-b border-navy mb-1 h-8"></div>
                    <div className="text-navy/60">Printed Name</div>
                  </div>
                  <div>
                    <div className="border-b border-navy mb-1 h-8"></div>
                    <div className="text-navy/60">Title (Trustee)</div>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-6 border-t border-navy/20">
                <h3 className="text-sm font-semibold text-navy mb-3">Witness</h3>
                <div className="grid grid-cols-2 gap-8" style={{ fontSize: '10pt' }}>
                  <div>
                    <div className="border-b border-navy mb-1 h-8"></div>
                    <div className="text-navy/60">Witness Signature</div>
                  </div>
                  <div>
                    <div className="border-b border-navy mb-1 h-8"></div>
                    <div className="text-navy/60">Date</div>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-6 border-t border-navy/20">
                <h3 className="text-sm font-semibold text-navy mb-3">Notary Acknowledgment</h3>
                <p className="text-navy/60 mb-4" style={{ fontSize: '9pt' }}>
                  State of _____________, County of _____________
                </p>
                <p className="text-navy/60 mb-4" style={{ fontSize: '9pt' }}>
                  On this _____ day of _____________, 20_____, before me personally appeared the above-named Trustee, known to me to be the person whose name is subscribed to the foregoing instrument, and acknowledged that they executed the same for the purposes therein contained.
                </p>
                <div className="grid grid-cols-2 gap-8" style={{ fontSize: '10pt' }}>
                  <div>
                    <div className="border-b border-navy mb-1 h-8"></div>
                    <div className="text-navy/60">Notary Public Signature</div>
                  </div>
                  <div>
                    <div className="border-b border-navy mb-1 h-8"></div>
                    <div className="text-navy/60">Commission Expiration</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 text-center">
                <p className="text-xs text-navy/40">TrustOffice — trustoffice.app</p>
              </div>
            </div>
          </div>
        )}

        {/* REAL ESTATE TRANSFER CHECKLIST */}
        {activePrint === 'checklist-real-estate' && (
          <div className="p-8" style={{ paddingLeft: '1.5in', paddingTop: '0.75in' }}>
            <div className="max-w-lg mx-auto">
              <h1 className="text-2xl font-bold text-navy mb-1" style={{ fontSize: '16pt' }}>
                Real Estate Transfer Checklist
              </h1>
              <p className="text-muted-foreground mb-6" style={{ fontSize: '10pt' }}>
                {coverData?.trust_name || 'Trust Name: _______________'}
              </p>
              <div className="w-16 h-0.5 bg-gold mb-6"></div>

              {[
                'Obtain certified copy of the Trust Agreement',
                'Obtain Certification of Trust (summary of key provisions)',
                'Prepare deed transferring property to/from the Trust',
                'Record deed with County Recorder\'s Office',
                'Update property insurance to reflect Trust ownership (add Trust as additional insured)',
                'Notify mortgage company of Trust ownership',
                'Update property tax records with new ownership',
                'File any required state transfer tax forms or exemptions',
                'Store recorded deed in Section 5 of this binder',
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-3 mb-3 py-2 border-b border-navy/10">
                  <div className="w-6 h-6 border-2 border-navy/30 flex-shrink-0 mt-0.5"></div>
                  <span className="text-navy" style={{ fontSize: '11pt' }}>{step}</span>
                </div>
              ))}

              <div className="mt-8 p-3 bg-subtle-bg text-muted-foreground" style={{ fontSize: '9pt' }}>
                <strong>Note:</strong> Requirements vary by state. Some states require specific deed forms or have transfer tax exemptions for trusts. Consult your attorney before recording.
              </div>
              <div className="mt-4 text-center text-xs text-navy/40">TrustOffice — trustoffice.app</div>
            </div>
          </div>
        )}

        {/* VEHICLE TRANSFER CHECKLIST */}
        {activePrint === 'checklist-vehicle' && (
          <div className="p-8" style={{ paddingLeft: '1.5in', paddingTop: '0.75in' }}>
            <div className="max-w-lg mx-auto">
              <h1 className="text-2xl font-bold text-navy mb-1" style={{ fontSize: '16pt' }}>
                Vehicle Transfer Checklist
              </h1>
              <p className="text-muted-foreground mb-6" style={{ fontSize: '10pt' }}>
                {coverData?.trust_name || 'Trust Name: _______________'}
              </p>
              <div className="w-16 h-0.5 bg-warning mb-6"></div>

              {[
                'Obtain Certification of Trust',
                'Complete vehicle title transfer form (available from your state DMV)',
                'Visit DMV with: Certification of Trust, current title, completed transfer form, and valid ID',
                'Pay title transfer fees',
                'Update vehicle insurance to reflect Trust ownership (add Trust as additional insured)',
                'Update vehicle registration to show Trust as owner',
                'Store new title and registration documents in Section 5 of this binder',
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-3 mb-3 py-2 border-b border-navy/10">
                  <div className="w-6 h-6 border-2 border-navy/30 flex-shrink-0 mt-0.5"></div>
                  <span className="text-navy" style={{ fontSize: '11pt' }}>{step}</span>
                </div>
              ))}

              <div className="mt-8 p-3 bg-subtle-bg text-muted-foreground" style={{ fontSize: '9pt' }}>
                <strong>Note:</strong> Some states require the trust name to appear on the title exactly as stated in the trust document. Contact your DMV for specific requirements.
              </div>
              <div className="mt-4 text-center text-xs text-navy/40">TrustOffice — trustoffice.app</div>
            </div>
          </div>
        )}

        {/* ==================== FINANCIAL RECORDS (MONEY) PRINTABLE ==================== */}
        {activePrint === 'money' && (
          <div className="p-8" style={{ paddingLeft: '1.5in', paddingTop: '0.75in' }}>
            <div className="max-w-xl mx-auto">
              <h1 className="text-2xl font-bold text-navy mb-1" style={{ fontSize: '16pt' }}>
                Financial Records
              </h1>
              <p className="text-muted-foreground mb-2" style={{ fontSize: '10pt' }}>
                {coverData?.trust_name || 'Trust Name: _______________'}
              </p>
              <div className="w-16 h-0.5 bg-gold mb-6"></div>

              {/* Transaction Ledger Summary */}
              <h2 className="text-lg font-semibold text-navy mb-2" style={{ fontSize: '13pt' }}>
                Transaction Ledger Summary
              </h2>
              {moneyData && moneyData.transactions.length > 0 ? (
                <>
                  <table className="w-full mb-4" style={{ fontSize: '10pt', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr className="border-b border-t border-navy/20">
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Date</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Direction</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Classification</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Memo</th>
                        <th className="text-right py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {moneyData.transactions.slice(0, 50).map((t, i) => (
                        <tr key={i} className="border-b border-navy/10">
                          <td className="py-1 font-mono text-navy" style={{ padding: '3px 8px' }}>{t.date || '—'}</td>
                          <td className="py-1 text-navy" style={{ padding: '3px 8px' }}>{t.direction || '—'}</td>
                          <td className="py-1 text-navy" style={{ padding: '3px 8px' }}>{t.governance_classification || '—'}</td>
                          <td className="py-1 text-navy/60" style={{ padding: '3px 8px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.purpose_memo || '—'}</td>
                          <td className="py-1 text-right font-mono text-navy" style={{ padding: '3px 8px' }}>${(t.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mb-6 p-3 border border-navy/20" style={{ fontSize: '10pt' }}>
                    <div className="flex justify-between py-0.5">
                      <span className="font-mono text-navy/60">Total Inflows:</span>
                      <span className="font-mono text-navy font-semibold">${moneyData.summary.totalInflows.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between py-0.5">
                      <span className="font-mono text-navy/60">Total Outflows:</span>
                      <span className="font-mono text-navy font-semibold">${moneyData.summary.totalOutflows.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between py-0.5 border-t border-navy/10" style={{ marginTop: '2px', paddingTop: '4px' }}>
                      <span className="font-mono text-navy font-semibold">Net Cash Flow:</span>
                      <span className="font-mono text-navy font-bold">${moneyData.summary.netFlow.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {moneyData.transactions.length > 50 && (
                      <div className="font-mono text-muted-foreground mt-1" style={{ fontSize: '9pt' }}>
                        Showing 50 of {moneyData.transactions.length} transactions.
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground mb-6" style={{ fontSize: '10pt' }}>No transactions recorded.</p>
              )}

              {/* Distribution History */}
              <h2 className="text-lg font-semibold text-navy mb-2" style={{ fontSize: '13pt' }}>
                Distribution History
              </h2>
              {moneyData && moneyData.distributions.length > 0 ? (
                <>
                  <table className="w-full mb-4" style={{ fontSize: '10pt', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Date</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Beneficiary</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Purpose</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Status</th>
                        <th className="text-right py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {moneyData.distributions.slice(0, 50).map((d, i) => (
                        <tr key={i} className="border-b border-navy/10">
                          <td className="py-1 font-mono text-navy" style={{ padding: '3px 8px' }}>{d.date || '—'}</td>
                          <td className="py-1 text-navy" style={{ padding: '3px 8px' }}>{d.beneficiary_name || '—'}</td>
                          <td className="py-1 text-navy/60" style={{ padding: '3px 8px' }}>{d.purpose_classification || '—'}</td>
                          <td className="py-1 text-navy/60" style={{ padding: '3px 8px' }}>{d.approved_at ? 'Approved' : 'Pending'}</td>
                          <td className="py-1 text-right font-mono text-navy" style={{ padding: '3px 8px' }}>${(d.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mb-6 p-3 border border-navy/20" style={{ fontSize: '10pt' }}>
                    <div className="flex justify-between py-0.5">
                      <span className="font-mono text-navy/60">Total Distributions:</span>
                      <span className="font-mono text-navy font-semibold">${moneyData.summary.distTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {moneyData.distributions.length > 50 && (
                      <div className="font-mono text-muted-foreground mt-1" style={{ fontSize: '9pt' }}>
                        Showing 50 of {moneyData.distributions.length} distributions.
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground mb-6" style={{ fontSize: '10pt' }}>No distributions recorded.</p>
              )}

              {/* Compensation Summary */}
              <h2 className="text-lg font-semibold text-navy mb-2" style={{ fontSize: '13pt' }}>
                Compensation Summary
              </h2>
              {moneyData && moneyData.compensation.length > 0 ? (
                <>
                  <table className="w-full mb-4" style={{ fontSize: '10pt', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Date</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Trustee</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Classification</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Over Plan?</th>
                        <th className="text-right py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {moneyData.compensation.slice(0, 50).map((c, i) => (
                        <tr key={i} className="border-b border-navy/10">
                          <td className="py-1 font-mono text-navy" style={{ padding: '3px 8px' }}>{c.date || '—'}</td>
                          <td className="py-1 text-navy" style={{ padding: '3px 8px' }}>{c.trustee_name || '—'}</td>
                          <td className="py-1 text-navy/60" style={{ padding: '3px 8px' }}>{c.classification_text || '—'}</td>
                          <td className="py-1 text-navy/60" style={{ padding: '3px 8px' }}>{c.exceeds_plan_flag ? 'Yes' : 'No'}</td>
                          <td className="py-1 text-right font-mono text-navy" style={{ padding: '3px 8px' }}>${(c.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mb-6 p-3 border border-navy/20" style={{ fontSize: '10pt' }}>
                    <div className="flex justify-between py-0.5">
                      <span className="font-mono text-navy/60">Total Compensation Paid:</span>
                      <span className="font-mono text-navy font-semibold">${moneyData.summary.compTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {moneyData.compensation.length > 50 && (
                      <div className="font-mono text-muted-foreground mt-1" style={{ fontSize: '9pt' }}>
                        Showing 50 of {moneyData.compensation.length} payments.
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground mb-6" style={{ fontSize: '10pt' }}>No compensation payments recorded.</p>
              )}

              {/* Investment Portfolio Summary */}
              <h2 className="text-lg font-semibold text-navy mb-2" style={{ fontSize: '13pt' }}>
                Investment Portfolio Summary
              </h2>
              {moneyData && moneyData.investments.length > 0 ? (
                <>
                  <table className="w-full mb-4" style={{ fontSize: '10pt', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Name</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Type</th>
                        <th className="text-right py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Cost Basis</th>
                        <th className="text-right py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Current Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {moneyData.investments.map((inv, i) => (
                        <tr key={i} className="border-b border-navy/10">
                          <td className="py-1 text-navy" style={{ padding: '3px 8px' }}>{inv.name || inv.institution || '—'}</td>
                          <td className="py-1 text-navy/60" style={{ padding: '3px 8px' }}>{inv.investment_type || inv.type || '—'}</td>
                          <td className="py-1 text-right font-mono text-navy" style={{ padding: '3px 8px' }}>${(inv.cost_basis || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="py-1 text-right font-mono text-navy" style={{ padding: '3px 8px' }}>${(inv.current_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mb-6 p-3 border border-navy/20" style={{ fontSize: '10pt' }}>
                    <div className="flex justify-between py-0.5">
                      <span className="font-mono text-navy/60">Total Cost Basis:</span>
                      <span className="font-mono text-navy font-semibold">${(moneyData.investmentSummary.total_cost_basis || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between py-0.5">
                      <span className="font-mono text-navy/60">Total Current Value:</span>
                      <span className="font-mono text-navy font-semibold">${(moneyData.investmentSummary.total_current_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between py-0.5">
                      <span className="font-mono text-navy/60">Total Return:</span>
                      <span className="font-mono text-navy font-semibold">${(moneyData.investmentSummary.total_return || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between py-0.5 border-t border-navy/10" style={{ marginTop: '2px', paddingTop: '4px' }}>
                      <span className="font-mono text-navy font-semibold">Return %:</span>
                      <span className="font-mono text-navy font-bold">{(moneyData.investmentSummary.total_return_pct || 0).toFixed(2)}%</span>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground mb-6" style={{ fontSize: '10pt' }}>No investments recorded.</p>
              )}

              <div className="mt-6 pt-4 border-t border-navy/20 text-center">
                <p className="text-xs text-navy/40">TrustOffice — trustoffice.app</p>
              </div>
            </div>
          </div>
        )}

        {/* ==================== ENTITY STRUCTURE PRINTABLE ==================== */}
        {activePrint === 'structure' && (
          <div className="p-8" style={{ paddingLeft: '1.5in', paddingTop: '0.75in' }}>
            <div className="max-w-xl mx-auto">
              <h1 className="text-2xl font-bold text-navy mb-1" style={{ fontSize: '16pt' }}>
                Entity Structure
              </h1>
              <p className="text-muted-foreground mb-2" style={{ fontSize: '10pt' }}>
                {coverData?.trust_name || 'Trust Name: _______________'}
              </p>
              <div className="w-16 h-0.5 bg-gold mb-6"></div>

              {/* Entity List Table */}
              <h2 className="text-lg font-semibold text-navy mb-2" style={{ fontSize: '13pt' }}>
                Entity List
              </h2>
              {structureData && structureData.entities.length > 0 ? (
                <table className="w-full mb-6" style={{ fontSize: '10pt', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Name</th>
                      <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Type</th>
                      <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Formation Date</th>
                      <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>EIN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {structureData.entities.map((e, i) => (
                      <tr key={i} className="border-b border-navy/10">
                        <td className="py-1 font-mono text-navy" style={{ padding: '3px 8px' }}>{e.name || '—'}</td>
                        <td className="py-1 font-mono text-navy/60" style={{ padding: '3px 8px' }}>{e.entity_type || '—'}</td>
                        <td className="py-1 font-mono text-navy/60" style={{ padding: '3px 8px' }}>{e.formation_date || '—'}</td>
                        <td className="py-1 font-mono text-navy/60" style={{ padding: '3px 8px' }}>{e.ein ? formatEIN(e.ein) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-muted-foreground mb-6" style={{ fontSize: '10pt' }}>No entities recorded.</p>
              )}

              {/* Beneficiary List Table */}
              <h2 className="text-lg font-semibold text-navy mb-2" style={{ fontSize: '13pt' }}>
                Beneficiary List
              </h2>
              {structureData && structureData.beneficiaries.length > 0 ? (
                <>
                  <table className="w-full mb-4" style={{ fontSize: '10pt', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Name</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Type</th>
                        <th className="text-right py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Units</th>
                        <th className="text-right py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Share %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {structureData.beneficiaries.map((b, i) => (
                        <tr key={i} className="border-b border-navy/10">
                          <td className="py-1 text-navy" style={{ padding: '3px 8px' }}>{b.holder_name || '—'}</td>
                          <td className="py-1 text-navy/60" style={{ padding: '3px 8px' }}>{b.holder_type || '—'}</td>
                          <td className="py-1 text-right font-mono text-navy" style={{ padding: '3px 8px' }}>{b.total_units || 0}</td>
                          <td className="py-1 text-right font-mono text-navy" style={{ padding: '3px 8px' }}>{(b.percentage || 0).toFixed(2)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {structureData.classBeneficiaries.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-navy mb-1" style={{ fontSize: '11pt' }}>Class Beneficiaries</h3>
                      <table className="w-full mb-4" style={{ fontSize: '10pt', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Class Type</th>
                            <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Description</th>
                            <th className="text-right py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Percentage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {structureData.classBeneficiaries.map((cb, i) => (
                            <tr key={i} className="border-b border-navy/10">
                              <td className="py-1 text-navy" style={{ padding: '3px 8px' }}>{cb.class_type_label || cb.class_type || '—'}</td>
                              <td className="py-1 text-navy/60" style={{ padding: '3px 8px' }}>{cb.description || '—'}</td>
                              <td className="py-1 text-right font-mono text-navy" style={{ padding: '3px 8px' }}>{(cb.percentage || 0)}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground mb-6" style={{ fontSize: '10pt' }}>No beneficiaries recorded.</p>
              )}

              {/* Schedule A Assets Table */}
              <h2 className="text-lg font-semibold text-navy mb-2" style={{ fontSize: '13pt' }}>
                Schedule A — Trust Assets
              </h2>
              {structureData && structureData.scheduleA.length > 0 ? (
                <>
                  <table className="w-full mb-4" style={{ fontSize: '10pt', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Description</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Category</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Location</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Status</th>
                        <th className="text-right py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {structureData.scheduleA.map((s, i) => (
                        <tr key={i} className="border-b border-navy/10">
                          <td className="py-1 text-navy" style={{ padding: '3px 8px' }}>{s.description || '—'}</td>
                          <td className="py-1 text-navy/60" style={{ padding: '3px 8px' }}>{s.category || '—'}</td>
                          <td className="py-1 text-navy/60" style={{ padding: '3px 8px' }}>{s.location || '—'}</td>
                          <td className="py-1 text-navy/60" style={{ padding: '3px 8px' }}>{s.status || '—'}</td>
                          <td className="py-1 text-right font-mono text-navy" style={{ padding: '3px 8px' }}>{s.approximate_value != null ? '$' + s.approximate_value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mb-6 p-3 border border-navy/20" style={{ fontSize: '10pt' }}>
                    <div className="flex justify-between py-0.5">
                      <span className="font-mono text-navy/60">Total Assets Value:</span>
                      <span className="font-mono text-navy font-semibold">${structureData.scheduleA.reduce((sum, s) => sum + (s.approximate_value || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground mb-6" style={{ fontSize: '10pt' }}>No Schedule A assets recorded.</p>
              )}

              {/* Communications Log Summary */}
              <h2 className="text-lg font-semibold text-navy mb-2" style={{ fontSize: '13pt' }}>
                Communications Log Summary
              </h2>
              {structureData && structureData.communications.length > 0 ? (
                <>
                  <table className="w-full mb-4" style={{ fontSize: '10pt', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Date</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Type</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Direction</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Subject</th>
                        <th className="text-left py-1 border-b border-navy/60 text-navy" style={{ padding: '4px 8px' }}>Action Required</th>
                      </tr>
                    </thead>
                    <tbody>
                      {structureData.communications.slice(0, 50).map((c, i) => (
                        <tr key={i} className="border-b border-navy/10">
                          <td className="py-1 font-mono text-navy" style={{ padding: '3px 8px' }}>{c.created_at ? c.created_at.split('T')[0] : '—'}</td>
                          <td className="py-1 text-navy/60" style={{ padding: '3px 8px' }}>{c.comm_type || '—'}</td>
                          <td className="py-1 text-navy/60" style={{ padding: '3px 8px' }}>{c.direction || '—'}</td>
                          <td className="py-1 text-navy" style={{ padding: '3px 8px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.subject || '—'}</td>
                          <td className="py-1 text-navy/60" style={{ padding: '3px 8px' }}>{c.action_required ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {structureData.communications.length > 50 && (
                    <div className="font-mono text-muted-foreground mb-6" style={{ fontSize: '9pt' }}>
                      Showing 50 of {structureData.communications.length} communications.
                    </div>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground mb-6" style={{ fontSize: '10pt' }}>No communications logged.</p>
              )}

              <div className="mt-6 pt-4 border-t border-navy/20 text-center">
                <p className="text-xs text-navy/40">TrustOffice — trustoffice.app</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PrintableBinderPage;